import { isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { partition } from 'lodash';

import { Auth0RoleName, Maybe, PodUser, TeamType, User } from '../gql/generated/graphql';
import { Pod } from '../gql/podGql';

import { LE_TIME_ZONE } from '../constants/LeaseEndInfo';
import { PodUsersByPod } from '../types/user';

import { filterUsersByRole } from './user';

type ActiveRepsOverride = {
  active_reps_override?: number | null;
  active_reps_override_set_at?: string | Date | null;
};

// Same rule as the API: a manager's override only counts on the LeaseEnd calendar day it
// was set, and never above the number of reps on the team. Null means no override today.
export const getActiveRepsOverrideToday = (
  { active_reps_override: override, active_reps_override_set_at: setAt }: ActiveRepsOverride,
  repCount: number,
): number | null => {
  if (override == null || setAt == null) {
    return null;
  }

  const setToday = isSameDay(
    utcToZonedTime(new Date(setAt), LE_TIME_ZONE),
    utcToZonedTime(new Date(), LE_TIME_ZONE),
  );
  return setToday ? Math.min(override, repCount) : null;
};

export enum PodModalFormRoles {
  Closer = 'closer',
  Setter = 'setter',
  SetterLead = 'setter_lead',
  TitleClerk = 'title_clerk',
  FundingClerk = 'funding_clerk',
}

const getTeamRoleNames = (
  teamType: TeamType,
  role: PodModalFormRoles,
): Auth0RoleName | Auth0RoleName[] => {
  switch (teamType) {
    case TeamType.D2d:
      if (role === PodModalFormRoles.Closer) {
        return Auth0RoleName.D2dCloser;
      }
      return Auth0RoleName.D2dSetter;
    case TeamType.Inbound:
      if (role === PodModalFormRoles.Closer) {
        return Auth0RoleName.InboundFinancialSpecialist;
      } // Financial specialists are pod agnostic
      if (role === PodModalFormRoles.SetterLead) {
        return Auth0RoleName.InboundSalesPodLead;
      }
      return Auth0RoleName.InboundSalesAdvisor;
    case TeamType.Outbound:
      if (role === PodModalFormRoles.SetterLead) {
        return Auth0RoleName.OutboundAdvisorManager;
      }
      if (role === PodModalFormRoles.Closer) {
        return Auth0RoleName.InboundFinancialSpecialist;
      } // Financial specialists are pod agnostic
      return Auth0RoleName.OutboundSetter;
    case TeamType.Titling:
      return Auth0RoleName.TitleClerk;
    case TeamType.FinancialSpecialist:
      if (role === PodModalFormRoles.SetterLead) {
        return Auth0RoleName.FsManager;
      }
      if (role === PodModalFormRoles.FundingClerk) {
        return [Auth0RoleName.FundingClerk, Auth0RoleName.FundingClerkManager];
      }
      return Auth0RoleName.InboundFinancialSpecialist;
    default:
      return Auth0RoleName.InboundSalesAdvisor;
  }
};

type PodMembers = NonNullable<Pod['usersWithoutTeamRole']>;

export const getAuth0RoleNameForTeam = (
  teamType: TeamType,
  role: PodModalFormRoles,
): Auth0RoleName[] => {
  const teamRoleNames = [getTeamRoleNames(teamType, role)].flat();

  return teamType === TeamType.FinancialSpecialist && role === PodModalFormRoles.Closer
    ? teamRoleNames
    : [...teamRoleNames, Auth0RoleName.Admin];
};

const teamTypesWithCloserPicker: TeamType[] = [TeamType.D2d, TeamType.FinancialSpecialist];

export const splitPodMembers = ({
  pod,
  users,
}: {
  pod: Pod;
  users: Maybe<User>[];
}): { closers: PodMembers; setters: PodMembers } => {
  const members = pod.usersWithoutTeamRole ?? [];
  if (!pod.team_type || !teamTypesWithCloserPicker.includes(pod.team_type)) {
    return { closers: [], setters: members };
  }

  const closerIds = filterUsersByRole(
    users,
    getAuth0RoleNameForTeam(pod.team_type, PodModalFormRoles.Closer),
  ).map((user) => user?.id);
  const [closers, setters] = partition(members, (member) => closerIds.includes(member.id));

  return { closers, setters };
};

export const groupTitlingUsersByPods = (allPodUsers: PodUser[]) => {
  const byPod = allPodUsers.reduce((acc, podUser) => {
    if (podUser.pod_id) {
      acc[podUser.pod_id] = acc[podUser.pod_id] ?? [];
      acc[podUser.pod_id].push(podUser);
    }

    return acc;
  }, {} as PodUsersByPod);

  return {
    ...byPod,
    all: allPodUsers,
    active: allPodUsers.filter((podUser) => !podUser.user?.deleted_at),
  };
};
